/**
 * Cache wrapper for mission-control logs. The leaf class `CachedMissionControlLogs` owns a
 * `RangeCache<MissionControlLog>` keyed by operator username; widening a previously-loaded
 * window for the same user fetches only the gap. Calls into `API.MissionControl.getLogs` for
 * the underlying HTTP fetch.
 */

import type { JobLifecycleDTO, MissionControlLog } from "@domain/MissionControl";
import { RangeCache } from "../cache/rangeCache";
import { TtlCache } from "../cache/ttlCache";
import { API } from "../api";

class CachedMissionControlLogs {
	private cache = new RangeCache<MissionControlLog>({
		timestampOf: (l) => new Date(l.timestamp).getTime(),
		// Logs occasionally arrive with a stable `id`; dedupe in case overlapping fetches return
		// the same row twice. Synthetic key when id is missing.
		keyOf: (l) => (l.id != null ? `id:${l.id}` : `${l.userId}::${l.timestamp}::${l.eventType ?? ""}`),
	});

	public getInRange(user: string, start: Date, end: Date): Promise<MissionControlLog[]> {
		return this.cache.query(user, [start.getTime(), end.getTime()], (key, gap) =>
			API.MissionControl.getLogs(key, new Date(gap[0]).toISOString(), new Date(gap[1]).toISOString()),
		);
	}

	public clear(): void {
		this.cache.clear();
	}
}

// Lifecycle data (Sentry logs/issues + SES emails) changes rarely — only while a job is
// actively processing — so a 10-min client-side TTL is safe on top of the API's own 5-min
// TTL cache; it saves a round-trip entirely on repeat panel opens/remounts for the same job
// within the window. `refresh()` is the user-facing bypass for "I'm watching an active job".
const LIFECYCLE_TTL_MS = 10 * 60 * 1000;

class CachedJobLifecycle {
	private cache = new TtlCache<string, JobLifecycleDTO>(LIFECYCLE_TTL_MS, (recordId) =>
		API.MissionControl.getJobLifecycle(recordId),
	);

	public get(recordId: string): Promise<JobLifecycleDTO> {
		return this.cache.get(recordId);
	}

	/** Bypasses both this cache and the API's server-side TTL cache, then repopulates this
	 *  cache with the fresh result (so subsequent `get`s within the TTL reuse it). */
	public async refresh(recordId: string): Promise<JobLifecycleDTO> {
		const dto = await API.MissionControl.getJobLifecycle(recordId, { refresh: true });
		this.cache.set(recordId, dto);
		return dto;
	}

	public clear(): void {
		this.cache.clear();
	}
}

export class CachedMissionControlAPI {
	public logs = new CachedMissionControlLogs();
	public lifecycle = new CachedJobLifecycle();
}
